import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { apiClient, auth, ApiError } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";
import { AuthShell, Field, inputCls, primaryBtn } from "./auth.sign-in";
export const Route = createFileRoute("/desktop/calendar")({
  validateSearch: (raw): { request?: string } => ({
    request:
      typeof raw.request === "string" && /^[0-9a-f-]{36}$/i.test(raw.request)
        ? raw.request
        : undefined,
  }),
  component: DesktopCalendar,
});
type RequestState = { status: string; error_code: string; return_url: string };
function DesktopCalendar() {
  const { request } = Route.useSearch();
  const store = useAuthStore();
  const [data, setData] = useState<RequestState | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [email, setEmail] = useState(""),
    [password, setPassword] = useState("");
  const path = "/api/v1/integrations/google/desktop/" + request;
  useEffect(() => {
    let active = true;
    setData(null);
    if (store.status === "authenticated" && request) {
      void apiClient
        .get<RequestState>(path)
        .then((r) => {
          if (active) {
            setData(r);
            setError("");
          }
        })
        .catch(() => {
          if (active)
            setError(
              "This request has expired or belongs to a different Jotty account. Use the account signed in on your Mac, or start again from Jotty.",
            );
        });
    }
    return () => {
      active = false;
    };
  }, [request, store.status, store.user?.id, path]);
  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await auth.signIn({ email, password });
      setPassword("");
      await store.setUser(user);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }
  async function connect() {
    setBusy(true);
    setError("");
    try {
      const r = await apiClient.get<{ authorization_url: string }>(
        "/api/v1/integrations/google/connect?desktop=" + request,
      );
      const url = new URL(r.authorization_url);
      if (url.protocol !== "https:" || url.hostname !== "accounts.google.com")
        throw Error();
      window.location.assign(url.href);
    } catch {
      setError(
        "Could not start Google authorization. Try again from your Mac.",
      );
      setBusy(false);
    }
  }
  async function cancel() {
    setBusy(true);
    try {
      await apiClient.post(path + "/cancel");
      setData(await apiClient.get<RequestState>(path));
      setError("");
    } catch {
      setError("Could not cancel. You can return to Jotty and try again.");
    } finally {
      setBusy(false);
    }
  }
  async function changeAccount() {
    setBusy(true);
    try {
      await auth.signOut();
      store.clear();
      setData(null);
      setError("");
    } catch {
      setError("Could not sign out. Please retry.");
    } finally {
      setBusy(false);
    }
  }
  let callback = "";
  if (data) {
    try {
      const u = new URL(data.return_url);
      if (
        u.protocol === "jotty:" &&
        u.hostname === "calendar" &&
        u.pathname === "/callback" &&
        u.searchParams.get("request") === request &&
        /^[A-Za-z0-9_-]{43}$/.test(u.searchParams.get("state") || "")
      )
        callback = u.href;
    } catch {
      /* Status refresh below reconciles a canceled request. */
    }
  }
  return (
    <AuthShell
      title={
        data?.status === "connected"
          ? "Google Calendar connected"
          : "Connect your calendar"
      }
      subtitle="Google is an optional calendar connection after Jotty sign-in. It does not become your Jotty login."
    >
      {!request ? (
        <p role="alert">Start the calendar connection from Jotty for Mac.</p>
      ) : store.status === "loading" || store.status === "unknown" ? (
        <p>Checking your Jotty account…</p>
      ) : store.status !== "authenticated" ? (
        <form onSubmit={signIn} className="flex flex-col gap-4">
          <p>Sign in with the same Jotty account you use on your Mac.</p>
          <Field label="Email">
            <input
              className={inputCls}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </Field>
          <Field label="Password">
            <input
              className={inputCls}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </Field>
          <button className={primaryBtn} disabled={busy}>
            Sign in to Jotty
          </button>
        </form>
      ) : (
        <>
          <p>Signed in as {store.user?.email}</p>
          {data?.status === "pending" ? (
            <>
              <p>
                Jotty will request read access to your calendar and access to
                files created or selected for the Google Docs exporter. It will
                not read your Gmail mailbox or start recording a meeting.
              </p>
              <button
                className={primaryBtn}
                disabled={busy}
                onClick={() => void connect()}
              >
                Continue to Google
              </button>
              <button disabled={busy} onClick={() => void cancel()}>
                Cancel connection
              </button>
            </>
          ) : data?.status === "connected" ? (
            <p>Return to Jotty to see your upcoming meetings.</p>
          ) : data ? (
            <p>
              {data.status === "cancelled"
                ? "Connection cancelled. You can keep taking notes without a calendar."
                : "The calendar connection did not finish. Return to Jotty and try again."}
            </p>
          ) : null}
          {callback && (
            <a className={primaryBtn} href={callback}>
              Return to Jotty
            </a>
          )}
          <button disabled={busy} onClick={() => void changeAccount()}>
            Use a different Jotty account
          </button>
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </AuthShell>
  );
}
